import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"
import { EnumDeliveryNotificationSetting } from "./enums/enumDeliveryNotificationSetting.generated";
import { EnumDistributionType } from "./enums/enumDistributionType.generated";
import { EnumEireMovementType } from "./enums/enumEireMovementType.generated";
import { EnumEmailDocumentGrouping } from "./enums/enumEmailDocumentGrouping.generated";
import { EnumExportIncotermCode } from "./enums/enumExportIncotermCode.generated";
import { EnumGermanBeerDutyMovementType } from "./enums/enumGermanBeerDutyMovementType.generated";
import { EnumStandardOrder } from "./enums/enumStandardOrder.generated";

// Model Objects

export class OutletSummary extends ModelObject<OutletSummary> {

    public id: Guid = Guid.createEmpty();
    /**
    * Internal name shown only to the users of the BrewMan system
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    */
    public displayName: string = "";
    /**
    * Default Invoice Address
    * Not Nullable
    * Max Length : 1000
    */
    public mainAddressLine1: string = "";
    /**
    * Default Invoice Address
    * Not Nullable
    * Max Length : 1000
    */
    public mainAddressLine2: string = "";
    /**
    * Default Invoice Address
    * Not Nullable
    * Max Length : 1000
    */
    public mainAddressLine3: string = "";
    /**
    * Default Invoice Address
    * Not Nullable
    * Max Length : 1000
    */
    public mainAddressLine4: string = "";
    /**
    * Default Invoice Address
    * Not Nullable
    * Max Length : 1000
    */
    public mainAddressLine5: string = "";
    /**
    * Default Invoice Address
    * Not Nullable
    * Max Length : 1000
    */
    public mainAddressLine6: string = "";
    /**
    * Default Invoice Address
    * Not Nullable
    * Max Length : 1000
    */
    public mainAddressPostcode: string = "";
    /**
    * Outlet Code
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    */
    public code: string = "";
    /**
    * Identifies if this is a Retail Outlet.
    */
    public isRetail: boolean = false;
    public isHidden: boolean = false;
    /**
    * Email Invoices
    */
    public emailInvoices: boolean = false;
    private _groupId: Guid | null = null;
    /**
    * Outlet Group
    */
    public get groupId(): Guid | null { return this._groupId }
    public set groupId(value: Guid | null) {
      this._groupId = value === undefined ? null : value;
    }
    private _salesCodeId: Guid | null = null;
    /**
    * Sales Code Id
    */
    public get salesCodeId(): Guid | null { return this._salesCodeId }
    public set salesCodeId(value: Guid | null) {
      this._salesCodeId = value === undefined ? null : value;
    }
    private _salesAreaId: Guid | null = null;
    /**
    * Sales Area Id
    */
    public get salesAreaId(): Guid | null { return this._salesAreaId }
    public set salesAreaId(value: Guid | null) {
      this._salesAreaId = value === undefined ? null : value;
    }
    /**
    * Outlet Rating
    */
    public outletRatingId: Guid = Guid.createEmpty();
    private _outletTypeId: Guid | null = null;
    /**
    * Outlet Type
    */
    public get outletTypeId(): Guid | null { return this._outletTypeId }
    public set outletTypeId(value: Guid | null) {
      this._outletTypeId = value === undefined ? null : value;
    }
    /**
    * Outlet Standard Order Type 0=Full Price  - Duty Paid 1=Price Excluding Duty - Duty Suspended 2=Full Price - Duty Paid Elsewhere 3=Export - Duty Suspended
    */
    public standardOrderType: EnumStandardOrder = EnumStandardOrder.fullPriceDutyPaid;
    private _deliveryAreaId: Guid | null = null;
    /**
    * Delivery Area
    */
    public get deliveryAreaId(): Guid | null { return this._deliveryAreaId }
    public set deliveryAreaId(value: Guid | null) {
      this._deliveryAreaId = value === undefined ? null : value;
    }
    /**
    * Price List
    */
    public priceListId: Guid = Guid.createEmpty();
    private _creditTermsId: Guid | null = null;
    /**
    * Credit Terms
    */
    public get creditTermsId(): Guid | null { return this._creditTermsId }
    public set creditTermsId(value: Guid | null) {
      this._creditTermsId = value === undefined ? null : value;
    }
    /**
    * Telephone number
    * Not Nullable
    * Max Length : 1000
    */
    public telephoneNumber: string = "";
    /**
    * Distribution Type : 0 = None, 1 = Delivery, 2 = Courier, 3 = Collection
    */
    public distributionType: EnumDistributionType = EnumDistributionType.none;
    public germanBeerDutyMovementType: EnumGermanBeerDutyMovementType = EnumGermanBeerDutyMovementType.none;
    /**
    * Default Stock Location
    */
    public stockLocationId: Guid = Guid.createEmpty();
    private _mainLongitude: number | null = null;
    /**
    * Longitude
    */
    public get mainLongitude(): number | null { return this._mainLongitude }
    public set mainLongitude(value: number | null) {
      this._mainLongitude = value === undefined ? null : value;
    }
    private _mainLatitude: number | null = null;
    /**
    * Latitude
    */
    public get mainLatitude(): number | null { return this._mainLatitude }
    public set mainLatitude(value: number | null) {
      this._mainLatitude = value === undefined ? null : value;
    }
    public eireDutyMovementType: EnumEireMovementType = EnumEireMovementType.unknown;
    /**
    * Product Requirements, eg minimum batch expiry lifetime. Not applicable if Outlet is in a group with UseGroupProductRequirementsForAllOutlets true
    * Not Nullable
    * Max Length : 1000
    */
    public productRequirements: string = "";
    public webAddress: string = "";
    /**
    * Email Address
    * Not Nullable
    * Max Length : 1000
    */
    public emailAddress: string = "";
    /**
    * Sales Notes
    * Not Nullable
    * Max Length : 100000
    */
    public salesNotes: string = "";
    private _lastOrderDeliveredDate: LocalDate | null = null;
    /**
    * The most recent delivery date of a completed order associated with this outlet. Captured by system not user editable.
    */
    public get lastOrderDeliveredDate(): LocalDate | null { return this._lastOrderDeliveredDate }
    public set lastOrderDeliveredDate(value: LocalDate | null) {
      this._lastOrderDeliveredDate = value === undefined ? null : value;
    }
    private _placedOnHoldAt: Instant | null = null;
    /**
    * The time when Outlet was set as On Hold
    */
    public get placedOnHoldAt(): Instant | null { return this._placedOnHoldAt }
    public set placedOnHoldAt(value: Instant | null) {
      this._placedOnHoldAt = value === undefined ? null : value;
    }
    /**
    * Identifies if this is Outlet allows order lines to be added qualifying for draught duty rates. For UK, if not allowed then new order lines will always add as DesignedForPump=false. 
    */
    public allowDraughtRelief: boolean = false;
    /**
    * The default phone number to use for delivery issues and updates.
    * Not Nullable
    * Max Length : 1000
    */
    public defaultDeliveryTelephoneNumber: string = "";
    /**
    * Text to appear on Delivery Notes
    * Not Nullable
    * Max Length : 100000
    */
    public deliveryNoteText: string = "";
    /**
    * Notes to appear on the Dray Run
    * Not Nullable
    * Max Length : 100000
    */
    public drayRunNotes: string = "";
    private _outletSubTypeId: Guid | null = null;
    /**
    * Outlet Sub Type
    */
    public get outletSubTypeId(): Guid | null { return this._outletSubTypeId }
    public set outletSubTypeId(value: Guid | null) {
      this._outletSubTypeId = value === undefined ? null : value;
    }
    /**
    * Timestamp at which the outlet was created on the system. Captured by system not user editable.
    */
    public createdAt: Instant = Instant.ofEpochMilli(0);
    private _creditLimit: number | null = null;
    /**
    * Credit Limit
    * Not Negative
    */
    public get creditLimit(): number | null { return this._creditLimit }
    public set creditLimit(value: number | null) {
      this._creditLimit = value === undefined ? null : value;
    }
    private _courierId: Guid | null = null;
    /**
    * Courier
    */
    public get courierId(): Guid | null { return this._courierId }
    public set courierId(value: Guid | null) {
      this._courierId = value === undefined ? null : value;
    }
    /**
    * Bitmap of Days on which orders are allowed to be dispatched (subject to deliver area dispatch days). Mon = 2, Tue = 4, ..., Sun = 128, aligns bit positions with ISO 8601 DayOfWeek numbering where Mon=1, Sun=7
    */
    public allowedDispatchDays: number = 0;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.displayName = jsonSerializedObject.displayName;
          this.mainAddressLine1 = jsonSerializedObject.mainAddressLine1;
          this.mainAddressLine2 = jsonSerializedObject.mainAddressLine2;
          this.mainAddressLine3 = jsonSerializedObject.mainAddressLine3;
          this.mainAddressLine4 = jsonSerializedObject.mainAddressLine4;
          this.mainAddressLine5 = jsonSerializedObject.mainAddressLine5;
          this.mainAddressLine6 = jsonSerializedObject.mainAddressLine6;
          this.mainAddressPostcode = jsonSerializedObject.mainAddressPostcode;
          this.code = jsonSerializedObject.code;
          this.isRetail = jsonSerializedObject.isRetail;
          this.isHidden = jsonSerializedObject.isHidden;
          this.emailInvoices = jsonSerializedObject.emailInvoices;
          this.groupId = jsonSerializedObject.groupId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.groupId);
          this.salesCodeId = jsonSerializedObject.salesCodeId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.salesCodeId);
          this.salesAreaId = jsonSerializedObject.salesAreaId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.salesAreaId);
          this.outletRatingId = Guid.parseOrEmpty(jsonSerializedObject.outletRatingId);
          this.outletTypeId = jsonSerializedObject.outletTypeId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.outletTypeId);
          this.standardOrderType = EnumStandardOrder.getByValue(jsonSerializedObject.standardOrderType, this.standardOrderType);
          this.deliveryAreaId = jsonSerializedObject.deliveryAreaId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.deliveryAreaId);
          this.priceListId = Guid.parseOrEmpty(jsonSerializedObject.priceListId);
          this.creditTermsId = jsonSerializedObject.creditTermsId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.creditTermsId);
          this.telephoneNumber = jsonSerializedObject.telephoneNumber;
          this.distributionType = EnumDistributionType.getByValue(jsonSerializedObject.distributionType, this.distributionType);
          this.germanBeerDutyMovementType = EnumGermanBeerDutyMovementType.getByValue(jsonSerializedObject.germanBeerDutyMovementType, this.germanBeerDutyMovementType);
          this.stockLocationId = Guid.parseOrEmpty(jsonSerializedObject.stockLocationId);
          this.mainLongitude = jsonSerializedObject.mainLongitude == null ? null : jsonSerializedObject.mainLongitude;
          this.mainLatitude = jsonSerializedObject.mainLatitude == null ? null : jsonSerializedObject.mainLatitude;
          this.eireDutyMovementType = EnumEireMovementType.getByValue(jsonSerializedObject.eireDutyMovementType, this.eireDutyMovementType);
          this.productRequirements = jsonSerializedObject.productRequirements;
          this.webAddress = jsonSerializedObject.webAddress;
          this.emailAddress = jsonSerializedObject.emailAddress;
          this.salesNotes = jsonSerializedObject.salesNotes;
          this.lastOrderDeliveredDate = jsonSerializedObject.lastOrderDeliveredDate == null ? null : LocalDate.parse(jsonSerializedObject.lastOrderDeliveredDate);
          this.placedOnHoldAt = jsonSerializedObject.placedOnHoldAt == null ? null : Instant.parse(jsonSerializedObject.placedOnHoldAt);
          this.allowDraughtRelief = jsonSerializedObject.allowDraughtRelief;
          this.defaultDeliveryTelephoneNumber = jsonSerializedObject.defaultDeliveryTelephoneNumber;
          this.deliveryNoteText = jsonSerializedObject.deliveryNoteText;
          this.drayRunNotes = jsonSerializedObject.drayRunNotes;
          this.outletSubTypeId = jsonSerializedObject.outletSubTypeId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.outletSubTypeId);
          this.createdAt = Instant.parse(jsonSerializedObject.createdAt);
          this.creditLimit = jsonSerializedObject.creditLimit == null ? null : jsonSerializedObject.creditLimit;
          this.courierId = jsonSerializedObject.courierId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.courierId);
          this.allowedDispatchDays = jsonSerializedObject.allowedDispatchDays;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.id = this.id.toString();
        toRet.displayName = this.displayName;
        toRet.mainAddressLine1 = this.mainAddressLine1;
        toRet.mainAddressLine2 = this.mainAddressLine2;
        toRet.mainAddressLine3 = this.mainAddressLine3;
        toRet.mainAddressLine4 = this.mainAddressLine4;
        toRet.mainAddressLine5 = this.mainAddressLine5;
        toRet.mainAddressLine6 = this.mainAddressLine6;
        toRet.mainAddressPostcode = this.mainAddressPostcode;
        toRet.code = this.code;
        toRet.isRetail = this.isRetail;
        toRet.isHidden = this.isHidden;
        toRet.emailInvoices = this.emailInvoices;
        toRet.groupId = this.groupId == null ? null : this.groupId.toString();
        toRet.salesCodeId = this.salesCodeId == null ? null : this.salesCodeId.toString();
        toRet.salesAreaId = this.salesAreaId == null ? null : this.salesAreaId.toString();
        toRet.outletRatingId = this.outletRatingId.toString();
        toRet.outletTypeId = this.outletTypeId == null ? null : this.outletTypeId.toString();
        toRet.standardOrderType = this.standardOrderType.value;
        toRet.deliveryAreaId = this.deliveryAreaId == null ? null : this.deliveryAreaId.toString();
        toRet.priceListId = this.priceListId.toString();
        toRet.creditTermsId = this.creditTermsId == null ? null : this.creditTermsId.toString();
        toRet.telephoneNumber = this.telephoneNumber;
        toRet.distributionType = this.distributionType.value;
        toRet.germanBeerDutyMovementType = this.germanBeerDutyMovementType.value;
        toRet.stockLocationId = this.stockLocationId.toString();
        toRet.mainLongitude = this.mainLongitude == null ? null : this.mainLongitude;
        toRet.mainLatitude = this.mainLatitude == null ? null : this.mainLatitude;
        toRet.eireDutyMovementType = this.eireDutyMovementType.value;
        toRet.productRequirements = this.productRequirements;
        toRet.webAddress = this.webAddress;
        toRet.emailAddress = this.emailAddress;
        toRet.salesNotes = this.salesNotes;
        toRet.lastOrderDeliveredDate = this.lastOrderDeliveredDate == null ? null : this.lastOrderDeliveredDate.toString();
        toRet.placedOnHoldAt = this.placedOnHoldAt == null ? null : this.placedOnHoldAt.toString();
        toRet.allowDraughtRelief = this.allowDraughtRelief;
        toRet.defaultDeliveryTelephoneNumber = this.defaultDeliveryTelephoneNumber;
        toRet.deliveryNoteText = this.deliveryNoteText;
        toRet.drayRunNotes = this.drayRunNotes;
        toRet.outletSubTypeId = this.outletSubTypeId == null ? null : this.outletSubTypeId.toString();
        toRet.createdAt = this.createdAt.toString();
        toRet.creditLimit = this.creditLimit == null ? null : this.creditLimit;
        toRet.courierId = this.courierId == null ? null : this.courierId.toString();
        toRet.allowedDispatchDays = this.allowedDispatchDays;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "id", rule: (v: any) => !v.isEmpty() || "OutletSummary.Id cannot be empty"},
            {field: "displayName", rule: (v: any) => v == null || v.length !== 0 || "DisplayName cannot be empty"},
            {field: "displayName", rule: (v: any) => v == null || v.length <= 1000 || "DisplayName has too many characters (1000)"},
            {field: "mainAddressLine1", rule: (v: any) => v == null || v.length <= 1000 || "MainAddressLine1 has too many characters (1000)"},
            {field: "mainAddressLine2", rule: (v: any) => v == null || v.length <= 1000 || "MainAddressLine2 has too many characters (1000)"},
            {field: "mainAddressLine3", rule: (v: any) => v == null || v.length <= 1000 || "MainAddressLine3 has too many characters (1000)"},
            {field: "mainAddressLine4", rule: (v: any) => v == null || v.length <= 1000 || "MainAddressLine4 has too many characters (1000)"},
            {field: "mainAddressLine5", rule: (v: any) => v == null || v.length <= 1000 || "MainAddressLine5 has too many characters (1000)"},
            {field: "mainAddressLine6", rule: (v: any) => v == null || v.length <= 1000 || "MainAddressLine6 has too many characters (1000)"},
            {field: "mainAddressPostcode", rule: (v: any) => v == null || v.length <= 1000 || "MainAddressPostcode has too many characters (1000)"},
            {field: "code", rule: (v: any) => v == null || v.length !== 0 || "Code cannot be empty"},
            {field: "code", rule: (v: any) => v == null || v.length <= 1000 || "Code has too many characters (1000)"},
            {field: "groupId", rule: (v: any) => v == null || !v.isEmpty() || "OutletSummary.GroupId cannot be empty"},
            {field: "salesCodeId", rule: (v: any) => v == null || !v.isEmpty() || "OutletSummary.SalesCodeId cannot be empty"},
            {field: "salesAreaId", rule: (v: any) => v == null || !v.isEmpty() || "OutletSummary.SalesAreaId cannot be empty"},
            {field: "outletRatingId", rule: (v: any) => !v.isEmpty() || "OutletSummary.OutletRatingId cannot be empty"},
            {field: "outletTypeId", rule: (v: any) => v == null || !v.isEmpty() || "OutletSummary.OutletTypeId cannot be empty"},
            {field: "deliveryAreaId", rule: (v: any) => v == null || !v.isEmpty() || "OutletSummary.DeliveryAreaId cannot be empty"},
            {field: "priceListId", rule: (v: any) => !v.isEmpty() || "OutletSummary.PriceListId cannot be empty"},
            {field: "creditTermsId", rule: (v: any) => v == null || !v.isEmpty() || "OutletSummary.CreditTermsId cannot be empty"},
            {field: "telephoneNumber", rule: (v: any) => v == null || v.length <= 1000 || "TelephoneNumber has too many characters (1000)"},
            {field: "stockLocationId", rule: (v: any) => !v.isEmpty() || "OutletSummary.StockLocationId cannot be empty"},
            {field: "productRequirements", rule: (v: any) => v == null || v.length <= 1000 || "ProductRequirements has too many characters (1000)"},
            {field: "webAddress", rule: (v: any) => v == null || v.length <= 1000 || "WebAddress has too many characters (1000)"},
            {field: "emailAddress", rule: (v: any) => v == null || v.length <= 1000 || "EmailAddress has too many characters (1000)"},
            {field: "salesNotes", rule: (v: any) => v == null || v.length <= 100000 || "SalesNotes has too many characters (100000)"},
            {field: "defaultDeliveryTelephoneNumber", rule: (v: any) => v == null || v.length <= 1000 || "DefaultDeliveryTelephoneNumber has too many characters (1000)"},
            {field: "deliveryNoteText", rule: (v: any) => v == null || v.length <= 100000 || "DeliveryNoteText has too many characters (100000)"},
            {field: "drayRunNotes", rule: (v: any) => v == null || v.length <= 100000 || "DrayRunNotes has too many characters (100000)"},
            {field: "outletSubTypeId", rule: (v: any) => v == null || !v.isEmpty() || "OutletSummary.OutletSubTypeId cannot be empty"},
            {field: "courierId", rule: (v: any) => v == null || !v.isEmpty() || "OutletSummary.CourierId cannot be empty"},
            {field: "allowedDispatchDays", rule: (v: any) => v == null || v >= -2147483648 || "AllowedDispatchDays is not greater than minimum allowed value: -2147483648"},
            {field: "allowedDispatchDays", rule: (v: any) => v == null || v <= 2147483647 || "AllowedDispatchDays is above maximum allowed value: 2147483647"},
    ];
}


export class Contact extends ModelObjectWithDirtyCheck<Contact> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    /**
    * Outlet Id
    */
    public outletId: Guid = Guid.createEmpty();
    /**
    * Forename
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    */
    public forename: string = "";
    /**
    * Surname
    * Not Nullable
    * Max Length : 1000
    */
    public surname: string = "";
    /**
    * Salutation
    * Not Nullable
    * Max Length : 1000
    */
    public salutation: string = "";
    /**
    * JobTitle
    * Not Nullable
    * Max Length : 1000
    */
    public jobTitle: string = "";
    /**
    * Telephone number
    * Not Nullable
    * Max Length : 1000
    */
    public phoneNumber: string = "";
    /**
    * Phone Extension
    * Not Nullable
    * Max Length : 1000
    */
    public phoneExtension: string = "";
    /**
    * Mobile Phone Number
    * Not Nullable
    * Max Length : 1000
    */
    public mobileNumber: string = "";
    /**
    * Email Address
    * Not Nullable
    * Max Length : 1000
    */
    public emailAddress: string = "";
    /**
    * Notes
    * Not Nullable
    * Max Length : 100000
    */
    public notes: string = "";
    /**
    * Address Line 1
    * Not Nullable
    * Max Length : 1000
    */
    public addressLine1: string = "";
    /**
    * Address Line 2
    * Not Nullable
    * Max Length : 1000
    */
    public addressLine2: string = "";
    /**
    * Address Line 3
    * Not Nullable
    * Max Length : 1000
    */
    public addressLine3: string = "";
    /**
    * Address Line 4
    * Not Nullable
    * Max Length : 1000
    */
    public addressLine4: string = "";
    /**
    * Address Line 5
    * Not Nullable
    * Max Length : 1000
    */
    public addressLine5: string = "";
    /**
    * Address Line 6
    * Not Nullable
    * Max Length : 1000
    */
    public addressLine6: string = "";
    /**
    * Postcode
    * Not Nullable
    * Max Length : 1000
    */
    public postcode: string = "";
    /**
    * Receives invoices
    */
    public areasInvoice: boolean = false;
    /**
    * Deals with orders
    */
    public areasOrders: boolean = false;
    /**
    * Receives delivery notes
    */
    public areasDeliveries: boolean = false;
    /**
    * Received marketing communication
    */
    public areasMarketing: boolean = false;
    /**
    * Deals with account queries
    */
    public areasAccounts: boolean = false;
    /**
    * Primary contact of the outlet. Should only have one primary contact per outlet.
    */
    public isPrimaryContact: boolean = false;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.outletId = Guid.parseOrEmpty(jsonSerializedObject.outletId);
          this.forename = jsonSerializedObject.forename;
          this.surname = jsonSerializedObject.surname;
          this.salutation = jsonSerializedObject.salutation;
          this.jobTitle = jsonSerializedObject.jobTitle;
          this.phoneNumber = jsonSerializedObject.phoneNumber;
          this.phoneExtension = jsonSerializedObject.phoneExtension;
          this.mobileNumber = jsonSerializedObject.mobileNumber;
          this.emailAddress = jsonSerializedObject.emailAddress;
          this.notes = jsonSerializedObject.notes;
          this.addressLine1 = jsonSerializedObject.addressLine1;
          this.addressLine2 = jsonSerializedObject.addressLine2;
          this.addressLine3 = jsonSerializedObject.addressLine3;
          this.addressLine4 = jsonSerializedObject.addressLine4;
          this.addressLine5 = jsonSerializedObject.addressLine5;
          this.addressLine6 = jsonSerializedObject.addressLine6;
          this.postcode = jsonSerializedObject.postcode;
          this.areasInvoice = jsonSerializedObject.areasInvoice;
          this.areasOrders = jsonSerializedObject.areasOrders;
          this.areasDeliveries = jsonSerializedObject.areasDeliveries;
          this.areasMarketing = jsonSerializedObject.areasMarketing;
          this.areasAccounts = jsonSerializedObject.areasAccounts;
          this.isPrimaryContact = jsonSerializedObject.isPrimaryContact;
        }

        this.setDirtyCheckpoint();
    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.outletId = this.outletId.toString();
        toRet.forename = this.forename;
        toRet.surname = this.surname;
        toRet.salutation = this.salutation;
        toRet.jobTitle = this.jobTitle;
        toRet.phoneNumber = this.phoneNumber;
        toRet.phoneExtension = this.phoneExtension;
        toRet.mobileNumber = this.mobileNumber;
        toRet.emailAddress = this.emailAddress;
        toRet.notes = this.notes;
        toRet.addressLine1 = this.addressLine1;
        toRet.addressLine2 = this.addressLine2;
        toRet.addressLine3 = this.addressLine3;
        toRet.addressLine4 = this.addressLine4;
        toRet.addressLine5 = this.addressLine5;
        toRet.addressLine6 = this.addressLine6;
        toRet.postcode = this.postcode;
        toRet.areasInvoice = this.areasInvoice;
        toRet.areasOrders = this.areasOrders;
        toRet.areasDeliveries = this.areasDeliveries;
        toRet.areasMarketing = this.areasMarketing;
        toRet.areasAccounts = this.areasAccounts;
        toRet.isPrimaryContact = this.isPrimaryContact;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "Contact.Id cannot be empty"},
            {field: "outletId", rule: (v: any) => !v.isEmpty() || "Contact.OutletId cannot be empty"},
            {field: "forename", rule: (v: any) => v == null || v.length !== 0 || "Forename cannot be empty"},
            {field: "forename", rule: (v: any) => v == null || v.length <= 1000 || "Forename has too many characters (1000)"},
            {field: "surname", rule: (v: any) => v == null || v.length <= 1000 || "Surname has too many characters (1000)"},
            {field: "salutation", rule: (v: any) => v == null || v.length <= 1000 || "Salutation has too many characters (1000)"},
            {field: "jobTitle", rule: (v: any) => v == null || v.length <= 1000 || "JobTitle has too many characters (1000)"},
            {field: "phoneNumber", rule: (v: any) => v == null || v.length <= 1000 || "PhoneNumber has too many characters (1000)"},
            {field: "phoneExtension", rule: (v: any) => v == null || v.length <= 1000 || "PhoneExtension has too many characters (1000)"},
            {field: "mobileNumber", rule: (v: any) => v == null || v.length <= 1000 || "MobileNumber has too many characters (1000)"},
            {field: "emailAddress", rule: (v: any) => v == null || v.length <= 1000 || "EmailAddress has too many characters (1000)"},
            {field: "notes", rule: (v: any) => v == null || v.length <= 100000 || "Notes has too many characters (100000)"},
            {field: "addressLine1", rule: (v: any) => v == null || v.length <= 1000 || "AddressLine1 has too many characters (1000)"},
            {field: "addressLine2", rule: (v: any) => v == null || v.length <= 1000 || "AddressLine2 has too many characters (1000)"},
            {field: "addressLine3", rule: (v: any) => v == null || v.length <= 1000 || "AddressLine3 has too many characters (1000)"},
            {field: "addressLine4", rule: (v: any) => v == null || v.length <= 1000 || "AddressLine4 has too many characters (1000)"},
            {field: "addressLine5", rule: (v: any) => v == null || v.length <= 1000 || "AddressLine5 has too many characters (1000)"},
            {field: "addressLine6", rule: (v: any) => v == null || v.length <= 1000 || "AddressLine6 has too many characters (1000)"},
            {field: "postcode", rule: (v: any) => v == null || v.length <= 1000 || "Postcode has too many characters (1000)"},
    ];
}


export class OutletReadOnly extends ModelObject<OutletReadOnly> {

    /**
    * Identifies if this is a Retail Outlet.
    */
    public isRetail: boolean = false;
    private _defaultDeliveryLatitude: number | null = null;
    /**
    * Default Latitude of delviery address (if known)
    */
    public get defaultDeliveryLatitude(): number | null { return this._defaultDeliveryLatitude }
    public set defaultDeliveryLatitude(value: number | null) {
      this._defaultDeliveryLatitude = value === undefined ? null : value;
    }
    private _defaultDeliveryLongitude: number | null = null;
    /**
    * Default Longitude of delviery address (if known)
    */
    public get defaultDeliveryLongitude(): number | null { return this._defaultDeliveryLongitude }
    public set defaultDeliveryLongitude(value: number | null) {
      this._defaultDeliveryLongitude = value === undefined ? null : value;
    }
    private _mainLongitude: number | null = null;
    /**
    * Longitude
    */
    public get mainLongitude(): number | null { return this._mainLongitude }
    public set mainLongitude(value: number | null) {
      this._mainLongitude = value === undefined ? null : value;
    }
    private _mainLatitude: number | null = null;
    /**
    * Latitude
    */
    public get mainLatitude(): number | null { return this._mainLatitude }
    public set mainLatitude(value: number | null) {
      this._mainLatitude = value === undefined ? null : value;
    }
    /**
    * Outlet Code
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    */
    public code: string = "";
    private _placedOnHoldAt: Instant | null = null;
    /**
    * The time when Outlet was set as On Hold
    */
    public get placedOnHoldAt(): Instant | null { return this._placedOnHoldAt }
    public set placedOnHoldAt(value: Instant | null) {
      this._placedOnHoldAt = value === undefined ? null : value;
    }
    private _placedOnHoldByUserId: Guid | null = null;
    /**
    * User who set this Outlet as On Hold
    */
    public get placedOnHoldByUserId(): Guid | null { return this._placedOnHoldByUserId }
    public set placedOnHoldByUserId(value: Guid | null) {
      this._placedOnHoldByUserId = value === undefined ? null : value;
    }
    /**
    * Timestamp at which the outlet was created on the system. Captured by system not user editable.
    */
    public createdAt: Instant = Instant.ofEpochMilli(0);

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.isRetail = jsonSerializedObject.isRetail;
          this.defaultDeliveryLatitude = jsonSerializedObject.defaultDeliveryLatitude == null ? null : jsonSerializedObject.defaultDeliveryLatitude;
          this.defaultDeliveryLongitude = jsonSerializedObject.defaultDeliveryLongitude == null ? null : jsonSerializedObject.defaultDeliveryLongitude;
          this.mainLongitude = jsonSerializedObject.mainLongitude == null ? null : jsonSerializedObject.mainLongitude;
          this.mainLatitude = jsonSerializedObject.mainLatitude == null ? null : jsonSerializedObject.mainLatitude;
          this.code = jsonSerializedObject.code;
          this.placedOnHoldAt = jsonSerializedObject.placedOnHoldAt == null ? null : Instant.parse(jsonSerializedObject.placedOnHoldAt);
          this.placedOnHoldByUserId = jsonSerializedObject.placedOnHoldByUserId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.placedOnHoldByUserId);
          this.createdAt = Instant.parse(jsonSerializedObject.createdAt);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.isRetail = this.isRetail;
        toRet.defaultDeliveryLatitude = this.defaultDeliveryLatitude == null ? null : this.defaultDeliveryLatitude;
        toRet.defaultDeliveryLongitude = this.defaultDeliveryLongitude == null ? null : this.defaultDeliveryLongitude;
        toRet.mainLongitude = this.mainLongitude == null ? null : this.mainLongitude;
        toRet.mainLatitude = this.mainLatitude == null ? null : this.mainLatitude;
        toRet.code = this.code;
        toRet.placedOnHoldAt = this.placedOnHoldAt == null ? null : this.placedOnHoldAt.toString();
        toRet.placedOnHoldByUserId = this.placedOnHoldByUserId == null ? null : this.placedOnHoldByUserId.toString();
        toRet.createdAt = this.createdAt.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "code", rule: (v: any) => v == null || v.length !== 0 || "Code cannot be empty"},
            {field: "code", rule: (v: any) => v == null || v.length <= 1000 || "Code has too many characters (1000)"},
            {field: "placedOnHoldByUserId", rule: (v: any) => v == null || !v.isEmpty() || "OutletReadOnly.PlacedOnHoldByUserId cannot be empty"},
    ];
}


export class Outlet extends ModelObjectWithDirtyCheck<Outlet> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    public isHidden: boolean = false;
    /**
    * Name
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    */
    public businessName: string = "";
    /**
    * Internal name shown only to the users of the BrewMan system
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    */
    public displayName: string = "";
    /**
    * Telephone number
    * Not Nullable
    * Max Length : 1000
    */
    public telephoneNumber: string = "";
    /**
    * Email Address
    * Not Nullable
    * Max Length : 1000
    */
    public emailAddress: string = "";
    private _groupId: Guid | null = null;
    /**
    * Outlet Group
    */
    public get groupId(): Guid | null { return this._groupId }
    public set groupId(value: Guid | null) {
      this._groupId = value === undefined ? null : value;
    }
    private _salesAreaId: Guid | null = null;
    /**
    * Sales Area Id
    */
    public get salesAreaId(): Guid | null { return this._salesAreaId }
    public set salesAreaId(value: Guid | null) {
      this._salesAreaId = value === undefined ? null : value;
    }
    private _salesCodeId: Guid | null = null;
    /**
    * Sales Code Id
    */
    public get salesCodeId(): Guid | null { return this._salesCodeId }
    public set salesCodeId(value: Guid | null) {
      this._salesCodeId = value === undefined ? null : value;
    }
    /**
    * Outlet Rating
    */
    public outletRatingId: Guid = Guid.createEmpty();
    private _outletTypeId: Guid | null = null;
    /**
    * Outlet Type
    */
    public get outletTypeId(): Guid | null { return this._outletTypeId }
    public set outletTypeId(value: Guid | null) {
      this._outletTypeId = value === undefined ? null : value;
    }
    /**
    * Default Stock Location
    */
    public stockLocationId: Guid = Guid.createEmpty();
    private _outletSubTypeId: Guid | null = null;
    /**
    * Outlet Sub Type
    */
    public get outletSubTypeId(): Guid | null { return this._outletSubTypeId }
    public set outletSubTypeId(value: Guid | null) {
      this._outletSubTypeId = value === undefined ? null : value;
    }
    /**
    * Default Invoice Address
    * Not Nullable
    * Max Length : 1000
    */
    public mainAddressLine1: string = "";
    /**
    * Default Invoice Address
    * Not Nullable
    * Max Length : 1000
    */
    public mainAddressLine2: string = "";
    /**
    * Default Invoice Address
    * Not Nullable
    * Max Length : 1000
    */
    public mainAddressLine3: string = "";
    /**
    * Default Invoice Address
    * Not Nullable
    * Max Length : 1000
    */
    public mainAddressLine4: string = "";
    /**
    * Default Invoice Address
    * Not Nullable
    * Max Length : 1000
    */
    public mainAddressLine5: string = "";
    /**
    * Default Invoice Address
    * Not Nullable
    * Max Length : 1000
    */
    public mainAddressLine6: string = "";
    /**
    * Default Invoice Address
    * Not Nullable
    * Max Length : 1000
    */
    public mainAddressPostcode: string = "";
    /**
    * Price List
    */
    public priceListId: Guid = Guid.createEmpty();
    /**
    * VAT Registration No.
    * Not Nullable
    * Max Length : 1000
    */
    public vatNumber: string = "";
    /**
    * Credit Terms Set
    */
    public creditTermsSet: boolean = false;
    private _creditTermsId: Guid | null = null;
    /**
    * Credit Terms
    */
    public get creditTermsId(): Guid | null { return this._creditTermsId }
    public set creditTermsId(value: Guid | null) {
      this._creditTermsId = value === undefined ? null : value;
    }
    private _creditLimit: number | null = null;
    /**
    * Credit Limit
    * Not Negative
    */
    public get creditLimit(): number | null { return this._creditLimit }
    public set creditLimit(value: number | null) {
      this._creditLimit = value === undefined ? null : value;
    }
    private _reviewDate: LocalDate | null = null;
    /**
    * Credit Review Date
    */
    public get reviewDate(): LocalDate | null { return this._reviewDate }
    public set reviewDate(value: LocalDate | null) {
      this._reviewDate = value === undefined ? null : value;
    }
    /**
    * Bank Name
    * Not Nullable
    * Max Length : 1000
    */
    public bankName: string = "";
    /**
    * Bank Sort Code
    * Not Nullable
    * Max Length : 1000
    */
    public bankSortCode: string = "";
    /**
    * Bank Account No
    * Not Nullable
    * Max Length : 1000
    */
    public bankAccountNumber: string = "";
    /**
    * Credit Term Notes
    * Not Nullable
    * Max Length : 100000
    */
    public creditTermNotes: string = "";
    /**
    * Email Invoices
    */
    public emailInvoices: boolean = false;
    private _defaultInvoiceTemplateId: Guid | null = null;
    /**
    * Default Invoice Template Id
    */
    public get defaultInvoiceTemplateId(): Guid | null { return this._defaultInvoiceTemplateId }
    public set defaultInvoiceTemplateId(value: Guid | null) {
      this._defaultInvoiceTemplateId = value === undefined ? null : value;
    }
    /**
    * Default invoice recipient
    * Not Nullable
    * Max Length : 1000
    */
    public defaultInvoiceRecipient: string = "";
    /**
    * The default email address invoices will be sent to if not updated by the order.
    * Not Nullable
    * Max Length : 1000
    */
    public defaultInvoiceEmailAddress: string = "";
    /**
    * HasDefaultInvoiceAddress
    */
    public hasDefaultInvoiceAddress: boolean = false;
    /**
    * Default Invoice Address
    * Not Nullable
    * Max Length : 1000
    */
    public defaultInvoiceAddressBusiness: string = "";
    /**
    * Default Invoice Address
    * Not Nullable
    * Max Length : 1000
    */
    public defaultInvoiceAddressLine1: string = "";
    /**
    * Default Invoice Address
    * Not Nullable
    * Max Length : 1000
    */
    public defaultInvoiceAddressLine2: string = "";
    /**
    * Default Invoice Address
    * Not Nullable
    * Max Length : 1000
    */
    public defaultInvoiceAddressLine3: string = "";
    /**
    * Default Invoice Address
    * Not Nullable
    * Max Length : 1000
    */
    public defaultInvoiceAddressLine4: string = "";
    /**
    * Default Invoice Address
    * Not Nullable
    * Max Length : 1000
    */
    public defaultInvoiceAddressLine5: string = "";
    /**
    * Default Invoice Address
    * Not Nullable
    * Max Length : 1000
    */
    public defaultInvoiceAddressLine6: string = "";
    /**
    * Default Invoice Address
    * Not Nullable
    * Max Length : 1000
    */
    public defaultInvoiceAddressPostcode: string = "";
    /**
    * Country Code (for Accounts)
    * Not Nullable
    * Max Length : 1000
    */
    public countryCode: string = "";
    /**
    * Accounts Department No (1-999)
    */
    public accountsDepartmentNumber: number = 0;
    private _defaultDespatchNoteTemplateId: Guid | null = null;
    /**
    * Default Despatch Note Template Id
    */
    public get defaultDespatchNoteTemplateId(): Guid | null { return this._defaultDespatchNoteTemplateId }
    public set defaultDespatchNoteTemplateId(value: Guid | null) {
      this._defaultDespatchNoteTemplateId = value === undefined ? null : value;
    }
    private _courierId: Guid | null = null;
    /**
    * Courier
    */
    public get courierId(): Guid | null { return this._courierId }
    public set courierId(value: Guid | null) {
      this._courierId = value === undefined ? null : value;
    }
    private _deliveryAreaId: Guid | null = null;
    /**
    * Delivery Area
    */
    public get deliveryAreaId(): Guid | null { return this._deliveryAreaId }
    public set deliveryAreaId(value: Guid | null) {
      this._deliveryAreaId = value === undefined ? null : value;
    }
    private _deliveryTimeEnd: LocalTime | null = null;
    /**
    * End of delivery window
    */
    public get deliveryTimeEnd(): LocalTime | null { return this._deliveryTimeEnd }
    public set deliveryTimeEnd(value: LocalTime | null) {
      this._deliveryTimeEnd = value === undefined ? null : value;
    }
    private _deliveryTimeStart: LocalTime | null = null;
    /**
    * Start of delivery window
    */
    public get deliveryTimeStart(): LocalTime | null { return this._deliveryTimeStart }
    public set deliveryTimeStart(value: LocalTime | null) {
      this._deliveryTimeStart = value === undefined ? null : value;
    }
    /**
    * Distribution Type : 0 = None, 1 = Delivery, 2 = Courier, 3 = Collection
    */
    public distributionType: EnumDistributionType = EnumDistributionType.none;
    /**
    * Stop Minutes
    */
    public stopMinutes: number = 0;
    /**
    * Text to appear on Delivery Notes
    * Not Nullable
    * Max Length : 100000
    */
    public deliveryNoteText: string = "";
    /**
    * Notes to appear on the Dray Run
    * Not Nullable
    * Max Length : 100000
    */
    public drayRunNotes: string = "";
    /**
    * EORI Number
    * Not Nullable
    * Max Length : 1000
    */
    public eoriNumber: string = "";
    /**
    * Exporting Incoterm Code : 0 = None, 1 = EXW, 2 = FCA, 3 = FAS, 4 = FOB, 5 = CFR, 6 = CIF, 7 = CIP, 8 = DPU, 9 = DDP, 10 = CPT, 11 = DAP
    */
    public exportingIncotermCode: EnumExportIncotermCode = EnumExportIncotermCode.none;
    /**
    * Exporting Incoterm Place
    * Not Nullable
    * Max Length : 1000
    */
    public exportingIncotermPlace: string = "";
    /**
    * The default email address to use for delivery issues and updates.
    * Not Nullable
    * Max Length : 1000
    */
    public defaultDeliveryEmailAddress: string = "";
    /**
    * The default phone number to use for delivery issues and updates.
    * Not Nullable
    * Max Length : 1000
    */
    public defaultDeliveryTelephoneNumber: string = "";
    /**
    * The recipient of the delivery
    * Not Nullable
    * Max Length : 1000
    */
    public defaultDeliveryRecipient: string = "";
    /**
    * HasDefaultDeliveryAddress
    */
    public hasDefaultDeliveryAddress: boolean = false;
    /**
    * Default Delivery Address
    * Not Nullable
    * Max Length : 1000
    */
    public defaultDeliveryAddressBusiness: string = "";
    /**
    * Default Delivery Address
    * Not Nullable
    * Max Length : 1000
    */
    public defaultDeliveryAddressLine1: string = "";
    /**
    * Default Delivery Address
    * Not Nullable
    * Max Length : 1000
    */
    public defaultDeliveryAddressLine2: string = "";
    /**
    * Default Delivery Address
    * Not Nullable
    * Max Length : 1000
    */
    public defaultDeliveryAddressLine3: string = "";
    /**
    * Default Delivery Address
    * Not Nullable
    * Max Length : 1000
    */
    public defaultDeliveryAddressLine4: string = "";
    /**
    * Default Delivery Address
    * Not Nullable
    * Max Length : 1000
    */
    public defaultDeliveryAddressLine5: string = "";
    /**
    * Default Delivery Address
    * Not Nullable
    * Max Length : 1000
    */
    public defaultDeliveryAddressLine6: string = "";
    /**
    * Default Delivery Address
    * Not Nullable
    * Max Length : 1000
    */
    public defaultDeliveryAddressPostcode: string = "";
    private _lastOrderDeliveredDate: LocalDate | null = null;
    /**
    * The most recent delivery date of a completed order associated with this outlet. Captured by system not user editable.
    */
    public get lastOrderDeliveredDate(): LocalDate | null { return this._lastOrderDeliveredDate }
    public set lastOrderDeliveredDate(value: LocalDate | null) {
      this._lastOrderDeliveredDate = value === undefined ? null : value;
    }
    /**
    * Outlet Standard Order Type 0=Full Price  - Duty Paid 1=Price Excluding Duty - Duty Suspended 2=Full Price - Duty Paid Elsewhere 3=Export - Duty Suspended
    */
    public standardOrderType: EnumStandardOrder = EnumStandardOrder.fullPriceDutyPaid;
    /**
    * VAT Code
    */
    public vatCodeId: Guid = Guid.createEmpty();
    /**
    * Application Form Returned
    */
    public applicationFormReturned: boolean = false;
    /**
    * Trading Terms
    * Not Nullable
    * Max Length : 100000
    */
    public tradingTerms: string = "";
    /**
    * Company Number of the outlet
    * Not Nullable
    * Max Length : 1000
    */
    public companyRegNumber: string = "";
    /**
    * Sales Notes
    * Not Nullable
    * Max Length : 100000
    */
    public salesNotes: string = "";
    public readOnly: OutletReadOnly = new OutletReadOnly();
    /**
    * International Bank Account Number
    * Not Nullable
    * Max Length : 1000
    */
    public iban: string = "";
    /**
    * Bank Identification Code
    * Not Nullable
    * Max Length : 1000
    */
    public bic: string = "";
    /**
    * The code used by Greene King to identify this outlet in exported data exchange files.
    * Not Nullable
    * Max Length : 1000
    */
    public gkSupplierCode: string = "";
    /**
    * The number of despatch notes to print
    * Not Negative
    */
    public despatchNoteCopiesToPrint: number = 0;
    /**
    * The number of invoice copies to print. (Not used if invoice goes to group).
    * Not Negative
    */
    public invoiceCopiesToPrint: number = 0;
    private _deliveryServiceId: Guid | null = null;
    /**
    * Delivery Service, if any, to apply by default to orders for this outlet. Has priority over group / courier / delivery area settings.
    */
    public get deliveryServiceId(): Guid | null { return this._deliveryServiceId }
    public set deliveryServiceId(value: Guid | null) {
      this._deliveryServiceId = value === undefined ? null : value;
    }
    public invoiceNotes: string = "";
    public germanBeerDutyMovementType: EnumGermanBeerDutyMovementType = EnumGermanBeerDutyMovementType.none;
    /**
    * Identifies how multiple documents are grouped into pdfs and emails when many documents are emailed at the same time.
    */
    public emailDocumentGrouping: EnumEmailDocumentGrouping = EnumEmailDocumentGrouping.unknown;
    /**
    * Sales Warning
    * Not Nullable
    * Max Length : 100000
    */
    public salesWarning: string = "";
    /**
    * Liquor Licence Number, only show for Australia and US region.
    * Not Nullable
    * Max Length : 1000
    */
    public liquorLicenceNumber: string = "";
    /**
    * Whether to send despatch notifications by WhatsApp for this outlet.
    */
    public whatsAppDispatchNotifications: EnumDeliveryNotificationSetting = EnumDeliveryNotificationSetting.unknown;
    /**
    * Phone number to send Dispatch WhatsApp to, if WhatsAppDispatchNotifications is ToSpecifiedAddress
    * Not Nullable
    * Max Length : 1000
    */
    public dispatchConfirmationWhatsAppNumber: string = "";
    /**
    * Whether to send despatch notifications by Email for this outlet.
    */
    public emailDespatchNotifications: EnumDeliveryNotificationSetting = EnumDeliveryNotificationSetting.unknown;
    private _emailDespatchNotificationTemplateId: Guid | null = null;
    /**
    * Template Id for sending despatch notification Emails.
    */
    public get emailDespatchNotificationTemplateId(): Guid | null { return this._emailDespatchNotificationTemplateId }
    public set emailDespatchNotificationTemplateId(value: Guid | null) {
      this._emailDespatchNotificationTemplateId = value === undefined ? null : value;
    }
    /**
    * Email address to send Despatch Email to, if EmailDespatchNotifications is ToSpecifiedAddress
    * Not Nullable
    * Max Length : 1000
    */
    public despatchConfirmationEmailAddress: string = "";
    /**
    * Whether to send order confirmation notifications by Email for this outlet.
    */
    public emailOrderConfirmation: EnumDeliveryNotificationSetting = EnumDeliveryNotificationSetting.unknown;
    private _emailOrderConfirmationTemplateId: Guid | null = null;
    /**
    * Template Id for sending order confirmation Emails.
    */
    public get emailOrderConfirmationTemplateId(): Guid | null { return this._emailOrderConfirmationTemplateId }
    public set emailOrderConfirmationTemplateId(value: Guid | null) {
      this._emailOrderConfirmationTemplateId = value === undefined ? null : value;
    }
    /**
    * Email address to send Order Confirmation Email to, if EmailOrderConfirmation is ToSpecifiedAddress
    * Not Nullable
    * Max Length : 1000
    */
    public orderConfirmationEmailAddress: string = "";
    /**
    * Tax number for posting to accounts. Only shown for countries which require a Tax Number of invoice recipients such as Germany (Steuernummer).
    * Not Nullable
    * Max Length : 1000
    */
    public invoiceRecipientTaxNumber: string = "";
    public eireDutyMovementType: EnumEireMovementType = EnumEireMovementType.unknown;
    /**
    * Product Requirements, eg minimum batch expiry lifetime. Not applicable if Outlet is in a group with UseGroupProductRequirementsForAllOutlets true
    * Not Nullable
    * Max Length : 1000
    */
    public productRequirements: string = "";
    public webAddress: string = "";
    public facebookWebAddress: string = "";
    public instagramWebAddress: string = "";
    public twitterWebAddress: string = "";
    public linkedInWebAddress: string = "";
    public tikTokWebAddress: string = "";
    public threadsWebAddress: string = "";
    public onHoldReason: string = "";
    /**
    * True if PlacedOnHoldAt is not null
    */
    public onHold: boolean = false;
    /**
    * Identifies if this is Outlet allows order lines to be added qualifying for draught duty rates. For UK, if not allowed then new order lines will always add as DesignedForPump=false. 
    */
    public allowDraughtRelief: boolean = false;
    /**
    * Whether the outlet's regional tax rate is calculated from regional tax (codefiles) or a rate applied manually. 
    */
    public usesAutomaticRegionalTax: boolean = false;
    public regionalTaxIds: (Guid)[] = [];
    /**
    * Identifies whether this outlet is household or non-household for EPR reporting.
    */
    public eprIsHousehold: boolean = false;
    /**
    * AWRS Number
    * Not Nullable
    * Max Length : 1000
    */
    public awrsNumber: string = "";
    /**
    * Indicates whether the outlet is a wholesaler. Used to identify that orders for this outlet is not the last wholesale sale under AU WET rules.
    */
    public isWholesaler: boolean = false;
    /**
    * Bitmap of Days on which orders are allowed to be dispatched (subject to deliver area dispatch days). Mon = 2, Tue = 4, ..., Sun = 128, aligns bit positions with ISO 8601 DayOfWeek numbering where Mon=1, Sun=7
    */
    public allowedDispatchDays: number = 0;
    /**
    * Invoices/credits for this outlet do not get posted to accounts, they go straight to history. 
    */
    public onlyPostToHistoryNotAccounts: boolean = false;
    private _customEmailSettingsId: Guid | null = null;
    /**
    * Email settings where we send all outlet related email from.
    */
    public get customEmailSettingsId(): Guid | null { return this._customEmailSettingsId }
    public set customEmailSettingsId(value: Guid | null) {
      this._customEmailSettingsId = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.isHidden = jsonSerializedObject.isHidden;
          this.businessName = jsonSerializedObject.businessName;
          this.displayName = jsonSerializedObject.displayName;
          this.telephoneNumber = jsonSerializedObject.telephoneNumber;
          this.emailAddress = jsonSerializedObject.emailAddress;
          this.groupId = jsonSerializedObject.groupId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.groupId);
          this.salesAreaId = jsonSerializedObject.salesAreaId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.salesAreaId);
          this.salesCodeId = jsonSerializedObject.salesCodeId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.salesCodeId);
          this.outletRatingId = Guid.parseOrEmpty(jsonSerializedObject.outletRatingId);
          this.outletTypeId = jsonSerializedObject.outletTypeId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.outletTypeId);
          this.stockLocationId = Guid.parseOrEmpty(jsonSerializedObject.stockLocationId);
          this.outletSubTypeId = jsonSerializedObject.outletSubTypeId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.outletSubTypeId);
          this.mainAddressLine1 = jsonSerializedObject.mainAddressLine1;
          this.mainAddressLine2 = jsonSerializedObject.mainAddressLine2;
          this.mainAddressLine3 = jsonSerializedObject.mainAddressLine3;
          this.mainAddressLine4 = jsonSerializedObject.mainAddressLine4;
          this.mainAddressLine5 = jsonSerializedObject.mainAddressLine5;
          this.mainAddressLine6 = jsonSerializedObject.mainAddressLine6;
          this.mainAddressPostcode = jsonSerializedObject.mainAddressPostcode;
          this.priceListId = Guid.parseOrEmpty(jsonSerializedObject.priceListId);
          this.vatNumber = jsonSerializedObject.vatNumber;
          this.creditTermsSet = jsonSerializedObject.creditTermsSet;
          this.creditTermsId = jsonSerializedObject.creditTermsId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.creditTermsId);
          this.creditLimit = jsonSerializedObject.creditLimit == null ? null : jsonSerializedObject.creditLimit;
          this.reviewDate = jsonSerializedObject.reviewDate == null ? null : LocalDate.parse(jsonSerializedObject.reviewDate);
          this.bankName = jsonSerializedObject.bankName;
          this.bankSortCode = jsonSerializedObject.bankSortCode;
          this.bankAccountNumber = jsonSerializedObject.bankAccountNumber;
          this.creditTermNotes = jsonSerializedObject.creditTermNotes;
          this.emailInvoices = jsonSerializedObject.emailInvoices;
          this.defaultInvoiceTemplateId = jsonSerializedObject.defaultInvoiceTemplateId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.defaultInvoiceTemplateId);
          this.defaultInvoiceRecipient = jsonSerializedObject.defaultInvoiceRecipient;
          this.defaultInvoiceEmailAddress = jsonSerializedObject.defaultInvoiceEmailAddress;
          this.hasDefaultInvoiceAddress = jsonSerializedObject.hasDefaultInvoiceAddress;
          this.defaultInvoiceAddressBusiness = jsonSerializedObject.defaultInvoiceAddressBusiness;
          this.defaultInvoiceAddressLine1 = jsonSerializedObject.defaultInvoiceAddressLine1;
          this.defaultInvoiceAddressLine2 = jsonSerializedObject.defaultInvoiceAddressLine2;
          this.defaultInvoiceAddressLine3 = jsonSerializedObject.defaultInvoiceAddressLine3;
          this.defaultInvoiceAddressLine4 = jsonSerializedObject.defaultInvoiceAddressLine4;
          this.defaultInvoiceAddressLine5 = jsonSerializedObject.defaultInvoiceAddressLine5;
          this.defaultInvoiceAddressLine6 = jsonSerializedObject.defaultInvoiceAddressLine6;
          this.defaultInvoiceAddressPostcode = jsonSerializedObject.defaultInvoiceAddressPostcode;
          this.countryCode = jsonSerializedObject.countryCode;
          this.accountsDepartmentNumber = jsonSerializedObject.accountsDepartmentNumber;
          this.defaultDespatchNoteTemplateId = jsonSerializedObject.defaultDespatchNoteTemplateId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.defaultDespatchNoteTemplateId);
          this.courierId = jsonSerializedObject.courierId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.courierId);
          this.deliveryAreaId = jsonSerializedObject.deliveryAreaId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.deliveryAreaId);
          this.deliveryTimeEnd = jsonSerializedObject.deliveryTimeEnd == null ? null : LocalTime.parse(jsonSerializedObject.deliveryTimeEnd);
          this.deliveryTimeStart = jsonSerializedObject.deliveryTimeStart == null ? null : LocalTime.parse(jsonSerializedObject.deliveryTimeStart);
          this.distributionType = EnumDistributionType.getByValue(jsonSerializedObject.distributionType, this.distributionType);
          this.stopMinutes = jsonSerializedObject.stopMinutes;
          this.deliveryNoteText = jsonSerializedObject.deliveryNoteText;
          this.drayRunNotes = jsonSerializedObject.drayRunNotes;
          this.eoriNumber = jsonSerializedObject.eoriNumber;
          this.exportingIncotermCode = EnumExportIncotermCode.getByValue(jsonSerializedObject.exportingIncotermCode, this.exportingIncotermCode);
          this.exportingIncotermPlace = jsonSerializedObject.exportingIncotermPlace;
          this.defaultDeliveryEmailAddress = jsonSerializedObject.defaultDeliveryEmailAddress;
          this.defaultDeliveryTelephoneNumber = jsonSerializedObject.defaultDeliveryTelephoneNumber;
          this.defaultDeliveryRecipient = jsonSerializedObject.defaultDeliveryRecipient;
          this.hasDefaultDeliveryAddress = jsonSerializedObject.hasDefaultDeliveryAddress;
          this.defaultDeliveryAddressBusiness = jsonSerializedObject.defaultDeliveryAddressBusiness;
          this.defaultDeliveryAddressLine1 = jsonSerializedObject.defaultDeliveryAddressLine1;
          this.defaultDeliveryAddressLine2 = jsonSerializedObject.defaultDeliveryAddressLine2;
          this.defaultDeliveryAddressLine3 = jsonSerializedObject.defaultDeliveryAddressLine3;
          this.defaultDeliveryAddressLine4 = jsonSerializedObject.defaultDeliveryAddressLine4;
          this.defaultDeliveryAddressLine5 = jsonSerializedObject.defaultDeliveryAddressLine5;
          this.defaultDeliveryAddressLine6 = jsonSerializedObject.defaultDeliveryAddressLine6;
          this.defaultDeliveryAddressPostcode = jsonSerializedObject.defaultDeliveryAddressPostcode;
          this.lastOrderDeliveredDate = jsonSerializedObject.lastOrderDeliveredDate == null ? null : LocalDate.parse(jsonSerializedObject.lastOrderDeliveredDate);
          this.standardOrderType = EnumStandardOrder.getByValue(jsonSerializedObject.standardOrderType, this.standardOrderType);
          this.vatCodeId = Guid.parseOrEmpty(jsonSerializedObject.vatCodeId);
          this.applicationFormReturned = jsonSerializedObject.applicationFormReturned;
          this.tradingTerms = jsonSerializedObject.tradingTerms;
          this.companyRegNumber = jsonSerializedObject.companyRegNumber;
          this.salesNotes = jsonSerializedObject.salesNotes;
          this.readOnly = new OutletReadOnly(jsonSerializedObject.readOnly);
          this.iban = jsonSerializedObject.iban;
          this.bic = jsonSerializedObject.bic;
          this.gkSupplierCode = jsonSerializedObject.gkSupplierCode;
          this.despatchNoteCopiesToPrint = jsonSerializedObject.despatchNoteCopiesToPrint;
          this.invoiceCopiesToPrint = jsonSerializedObject.invoiceCopiesToPrint;
          this.deliveryServiceId = jsonSerializedObject.deliveryServiceId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.deliveryServiceId);
          this.invoiceNotes = jsonSerializedObject.invoiceNotes;
          this.germanBeerDutyMovementType = EnumGermanBeerDutyMovementType.getByValue(jsonSerializedObject.germanBeerDutyMovementType, this.germanBeerDutyMovementType);
          this.emailDocumentGrouping = EnumEmailDocumentGrouping.getByValue(jsonSerializedObject.emailDocumentGrouping, this.emailDocumentGrouping);
          this.salesWarning = jsonSerializedObject.salesWarning;
          this.liquorLicenceNumber = jsonSerializedObject.liquorLicenceNumber;
          this.whatsAppDispatchNotifications = EnumDeliveryNotificationSetting.getByValue(jsonSerializedObject.whatsAppDispatchNotifications, this.whatsAppDispatchNotifications);
          this.dispatchConfirmationWhatsAppNumber = jsonSerializedObject.dispatchConfirmationWhatsAppNumber;
          this.emailDespatchNotifications = EnumDeliveryNotificationSetting.getByValue(jsonSerializedObject.emailDespatchNotifications, this.emailDespatchNotifications);
          this.emailDespatchNotificationTemplateId = jsonSerializedObject.emailDespatchNotificationTemplateId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.emailDespatchNotificationTemplateId);
          this.despatchConfirmationEmailAddress = jsonSerializedObject.despatchConfirmationEmailAddress;
          this.emailOrderConfirmation = EnumDeliveryNotificationSetting.getByValue(jsonSerializedObject.emailOrderConfirmation, this.emailOrderConfirmation);
          this.emailOrderConfirmationTemplateId = jsonSerializedObject.emailOrderConfirmationTemplateId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.emailOrderConfirmationTemplateId);
          this.orderConfirmationEmailAddress = jsonSerializedObject.orderConfirmationEmailAddress;
          this.invoiceRecipientTaxNumber = jsonSerializedObject.invoiceRecipientTaxNumber;
          this.eireDutyMovementType = EnumEireMovementType.getByValue(jsonSerializedObject.eireDutyMovementType, this.eireDutyMovementType);
          this.productRequirements = jsonSerializedObject.productRequirements;
          this.webAddress = jsonSerializedObject.webAddress;
          this.facebookWebAddress = jsonSerializedObject.facebookWebAddress;
          this.instagramWebAddress = jsonSerializedObject.instagramWebAddress;
          this.twitterWebAddress = jsonSerializedObject.twitterWebAddress;
          this.linkedInWebAddress = jsonSerializedObject.linkedInWebAddress;
          this.tikTokWebAddress = jsonSerializedObject.tikTokWebAddress;
          this.threadsWebAddress = jsonSerializedObject.threadsWebAddress;
          this.onHoldReason = jsonSerializedObject.onHoldReason;
          this.onHold = jsonSerializedObject.onHold;
          this.allowDraughtRelief = jsonSerializedObject.allowDraughtRelief;
          this.usesAutomaticRegionalTax = jsonSerializedObject.usesAutomaticRegionalTax;
          this.regionalTaxIds = jsonSerializedObject.regionalTaxIds.map((v: any) => Guid.parseOrEmpty(v));
          this.eprIsHousehold = jsonSerializedObject.eprIsHousehold;
          this.awrsNumber = jsonSerializedObject.awrsNumber;
          this.isWholesaler = jsonSerializedObject.isWholesaler;
          this.allowedDispatchDays = jsonSerializedObject.allowedDispatchDays;
          this.onlyPostToHistoryNotAccounts = jsonSerializedObject.onlyPostToHistoryNotAccounts;
          this.customEmailSettingsId = jsonSerializedObject.customEmailSettingsId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.customEmailSettingsId);
        }

        this.setDirtyCheckpoint();
    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.isHidden = this.isHidden;
        toRet.businessName = this.businessName;
        toRet.displayName = this.displayName;
        toRet.telephoneNumber = this.telephoneNumber;
        toRet.emailAddress = this.emailAddress;
        toRet.groupId = this.groupId == null ? null : this.groupId.toString();
        toRet.salesAreaId = this.salesAreaId == null ? null : this.salesAreaId.toString();
        toRet.salesCodeId = this.salesCodeId == null ? null : this.salesCodeId.toString();
        toRet.outletRatingId = this.outletRatingId.toString();
        toRet.outletTypeId = this.outletTypeId == null ? null : this.outletTypeId.toString();
        toRet.stockLocationId = this.stockLocationId.toString();
        toRet.outletSubTypeId = this.outletSubTypeId == null ? null : this.outletSubTypeId.toString();
        toRet.mainAddressLine1 = this.mainAddressLine1;
        toRet.mainAddressLine2 = this.mainAddressLine2;
        toRet.mainAddressLine3 = this.mainAddressLine3;
        toRet.mainAddressLine4 = this.mainAddressLine4;
        toRet.mainAddressLine5 = this.mainAddressLine5;
        toRet.mainAddressLine6 = this.mainAddressLine6;
        toRet.mainAddressPostcode = this.mainAddressPostcode;
        toRet.priceListId = this.priceListId.toString();
        toRet.vatNumber = this.vatNumber;
        toRet.creditTermsSet = this.creditTermsSet;
        toRet.creditTermsId = this.creditTermsId == null ? null : this.creditTermsId.toString();
        toRet.creditLimit = this.creditLimit == null ? null : this.creditLimit;
        toRet.reviewDate = this.reviewDate == null ? null : this.reviewDate.toString();
        toRet.bankName = this.bankName;
        toRet.bankSortCode = this.bankSortCode;
        toRet.bankAccountNumber = this.bankAccountNumber;
        toRet.creditTermNotes = this.creditTermNotes;
        toRet.emailInvoices = this.emailInvoices;
        toRet.defaultInvoiceTemplateId = this.defaultInvoiceTemplateId == null ? null : this.defaultInvoiceTemplateId.toString();
        toRet.defaultInvoiceRecipient = this.defaultInvoiceRecipient;
        toRet.defaultInvoiceEmailAddress = this.defaultInvoiceEmailAddress;
        toRet.hasDefaultInvoiceAddress = this.hasDefaultInvoiceAddress;
        toRet.defaultInvoiceAddressBusiness = this.defaultInvoiceAddressBusiness;
        toRet.defaultInvoiceAddressLine1 = this.defaultInvoiceAddressLine1;
        toRet.defaultInvoiceAddressLine2 = this.defaultInvoiceAddressLine2;
        toRet.defaultInvoiceAddressLine3 = this.defaultInvoiceAddressLine3;
        toRet.defaultInvoiceAddressLine4 = this.defaultInvoiceAddressLine4;
        toRet.defaultInvoiceAddressLine5 = this.defaultInvoiceAddressLine5;
        toRet.defaultInvoiceAddressLine6 = this.defaultInvoiceAddressLine6;
        toRet.defaultInvoiceAddressPostcode = this.defaultInvoiceAddressPostcode;
        toRet.countryCode = this.countryCode;
        toRet.accountsDepartmentNumber = this.accountsDepartmentNumber;
        toRet.defaultDespatchNoteTemplateId = this.defaultDespatchNoteTemplateId == null ? null : this.defaultDespatchNoteTemplateId.toString();
        toRet.courierId = this.courierId == null ? null : this.courierId.toString();
        toRet.deliveryAreaId = this.deliveryAreaId == null ? null : this.deliveryAreaId.toString();
        toRet.deliveryTimeEnd = this.deliveryTimeEnd == null ? null : this.deliveryTimeEnd.toString();
        toRet.deliveryTimeStart = this.deliveryTimeStart == null ? null : this.deliveryTimeStart.toString();
        toRet.distributionType = this.distributionType.value;
        toRet.stopMinutes = this.stopMinutes;
        toRet.deliveryNoteText = this.deliveryNoteText;
        toRet.drayRunNotes = this.drayRunNotes;
        toRet.eoriNumber = this.eoriNumber;
        toRet.exportingIncotermCode = this.exportingIncotermCode.value;
        toRet.exportingIncotermPlace = this.exportingIncotermPlace;
        toRet.defaultDeliveryEmailAddress = this.defaultDeliveryEmailAddress;
        toRet.defaultDeliveryTelephoneNumber = this.defaultDeliveryTelephoneNumber;
        toRet.defaultDeliveryRecipient = this.defaultDeliveryRecipient;
        toRet.hasDefaultDeliveryAddress = this.hasDefaultDeliveryAddress;
        toRet.defaultDeliveryAddressBusiness = this.defaultDeliveryAddressBusiness;
        toRet.defaultDeliveryAddressLine1 = this.defaultDeliveryAddressLine1;
        toRet.defaultDeliveryAddressLine2 = this.defaultDeliveryAddressLine2;
        toRet.defaultDeliveryAddressLine3 = this.defaultDeliveryAddressLine3;
        toRet.defaultDeliveryAddressLine4 = this.defaultDeliveryAddressLine4;
        toRet.defaultDeliveryAddressLine5 = this.defaultDeliveryAddressLine5;
        toRet.defaultDeliveryAddressLine6 = this.defaultDeliveryAddressLine6;
        toRet.defaultDeliveryAddressPostcode = this.defaultDeliveryAddressPostcode;
        toRet.lastOrderDeliveredDate = this.lastOrderDeliveredDate == null ? null : this.lastOrderDeliveredDate.toString();
        toRet.standardOrderType = this.standardOrderType.value;
        toRet.vatCodeId = this.vatCodeId.toString();
        toRet.applicationFormReturned = this.applicationFormReturned;
        toRet.tradingTerms = this.tradingTerms;
        toRet.companyRegNumber = this.companyRegNumber;
        toRet.salesNotes = this.salesNotes;
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        toRet.iban = this.iban;
        toRet.bic = this.bic;
        toRet.gkSupplierCode = this.gkSupplierCode;
        toRet.despatchNoteCopiesToPrint = this.despatchNoteCopiesToPrint;
        toRet.invoiceCopiesToPrint = this.invoiceCopiesToPrint;
        toRet.deliveryServiceId = this.deliveryServiceId == null ? null : this.deliveryServiceId.toString();
        toRet.invoiceNotes = this.invoiceNotes;
        toRet.germanBeerDutyMovementType = this.germanBeerDutyMovementType.value;
        toRet.emailDocumentGrouping = this.emailDocumentGrouping.value;
        toRet.salesWarning = this.salesWarning;
        toRet.liquorLicenceNumber = this.liquorLicenceNumber;
        toRet.whatsAppDispatchNotifications = this.whatsAppDispatchNotifications.value;
        toRet.dispatchConfirmationWhatsAppNumber = this.dispatchConfirmationWhatsAppNumber;
        toRet.emailDespatchNotifications = this.emailDespatchNotifications.value;
        toRet.emailDespatchNotificationTemplateId = this.emailDespatchNotificationTemplateId == null ? null : this.emailDespatchNotificationTemplateId.toString();
        toRet.despatchConfirmationEmailAddress = this.despatchConfirmationEmailAddress;
        toRet.emailOrderConfirmation = this.emailOrderConfirmation.value;
        toRet.emailOrderConfirmationTemplateId = this.emailOrderConfirmationTemplateId == null ? null : this.emailOrderConfirmationTemplateId.toString();
        toRet.orderConfirmationEmailAddress = this.orderConfirmationEmailAddress;
        toRet.invoiceRecipientTaxNumber = this.invoiceRecipientTaxNumber;
        toRet.eireDutyMovementType = this.eireDutyMovementType.value;
        toRet.productRequirements = this.productRequirements;
        toRet.webAddress = this.webAddress;
        toRet.facebookWebAddress = this.facebookWebAddress;
        toRet.instagramWebAddress = this.instagramWebAddress;
        toRet.twitterWebAddress = this.twitterWebAddress;
        toRet.linkedInWebAddress = this.linkedInWebAddress;
        toRet.tikTokWebAddress = this.tikTokWebAddress;
        toRet.threadsWebAddress = this.threadsWebAddress;
        toRet.onHoldReason = this.onHoldReason;
        toRet.onHold = this.onHold;
        toRet.allowDraughtRelief = this.allowDraughtRelief;
        toRet.usesAutomaticRegionalTax = this.usesAutomaticRegionalTax;
        toRet.regionalTaxIds = this.regionalTaxIds.map(v => v.toString());
        toRet.eprIsHousehold = this.eprIsHousehold;
        toRet.awrsNumber = this.awrsNumber;
        toRet.isWholesaler = this.isWholesaler;
        toRet.allowedDispatchDays = this.allowedDispatchDays;
        toRet.onlyPostToHistoryNotAccounts = this.onlyPostToHistoryNotAccounts;
        toRet.customEmailSettingsId = this.customEmailSettingsId == null ? null : this.customEmailSettingsId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "Outlet.Id cannot be empty"},
            {field: "businessName", rule: (v: any) => v == null || v.length !== 0 || "BusinessName cannot be empty"},
            {field: "businessName", rule: (v: any) => v == null || v.length <= 1000 || "BusinessName has too many characters (1000)"},
            {field: "displayName", rule: (v: any) => v == null || v.length !== 0 || "DisplayName cannot be empty"},
            {field: "displayName", rule: (v: any) => v == null || v.length <= 1000 || "DisplayName has too many characters (1000)"},
            {field: "telephoneNumber", rule: (v: any) => v == null || v.length <= 1000 || "TelephoneNumber has too many characters (1000)"},
            {field: "emailAddress", rule: (v: any) => v == null || v.length <= 1000 || "EmailAddress has too many characters (1000)"},
            {field: "groupId", rule: (v: any) => v == null || !v.isEmpty() || "Outlet.GroupId cannot be empty"},
            {field: "salesAreaId", rule: (v: any) => v == null || !v.isEmpty() || "Outlet.SalesAreaId cannot be empty"},
            {field: "salesCodeId", rule: (v: any) => v == null || !v.isEmpty() || "Outlet.SalesCodeId cannot be empty"},
            {field: "outletRatingId", rule: (v: any) => !v.isEmpty() || "Outlet.OutletRatingId cannot be empty"},
            {field: "outletTypeId", rule: (v: any) => v == null || !v.isEmpty() || "Outlet.OutletTypeId cannot be empty"},
            {field: "stockLocationId", rule: (v: any) => !v.isEmpty() || "Outlet.StockLocationId cannot be empty"},
            {field: "outletSubTypeId", rule: (v: any) => v == null || !v.isEmpty() || "Outlet.OutletSubTypeId cannot be empty"},
            {field: "mainAddressLine1", rule: (v: any) => v == null || v.length <= 1000 || "MainAddressLine1 has too many characters (1000)"},
            {field: "mainAddressLine2", rule: (v: any) => v == null || v.length <= 1000 || "MainAddressLine2 has too many characters (1000)"},
            {field: "mainAddressLine3", rule: (v: any) => v == null || v.length <= 1000 || "MainAddressLine3 has too many characters (1000)"},
            {field: "mainAddressLine4", rule: (v: any) => v == null || v.length <= 1000 || "MainAddressLine4 has too many characters (1000)"},
            {field: "mainAddressLine5", rule: (v: any) => v == null || v.length <= 1000 || "MainAddressLine5 has too many characters (1000)"},
            {field: "mainAddressLine6", rule: (v: any) => v == null || v.length <= 1000 || "MainAddressLine6 has too many characters (1000)"},
            {field: "mainAddressPostcode", rule: (v: any) => v == null || v.length <= 1000 || "MainAddressPostcode has too many characters (1000)"},
            {field: "priceListId", rule: (v: any) => !v.isEmpty() || "Outlet.PriceListId cannot be empty"},
            {field: "vatNumber", rule: (v: any) => v == null || v.length <= 1000 || "VatNumber has too many characters (1000)"},
            {field: "creditTermsId", rule: (v: any) => v == null || !v.isEmpty() || "Outlet.CreditTermsId cannot be empty"},
            {field: "bankName", rule: (v: any) => v == null || v.length <= 1000 || "BankName has too many characters (1000)"},
            {field: "bankSortCode", rule: (v: any) => v == null || v.length <= 1000 || "BankSortCode has too many characters (1000)"},
            {field: "bankAccountNumber", rule: (v: any) => v == null || v.length <= 1000 || "BankAccountNumber has too many characters (1000)"},
            {field: "creditTermNotes", rule: (v: any) => v == null || v.length <= 100000 || "CreditTermNotes has too many characters (100000)"},
            {field: "defaultInvoiceTemplateId", rule: (v: any) => v == null || !v.isEmpty() || "Outlet.DefaultInvoiceTemplateId cannot be empty"},
            {field: "defaultInvoiceRecipient", rule: (v: any) => v == null || v.length <= 1000 || "DefaultInvoiceRecipient has too many characters (1000)"},
            {field: "defaultInvoiceEmailAddress", rule: (v: any) => v == null || v.length <= 1000 || "DefaultInvoiceEmailAddress has too many characters (1000)"},
            {field: "defaultInvoiceAddressBusiness", rule: (v: any) => v == null || v.length <= 1000 || "DefaultInvoiceAddressBusiness has too many characters (1000)"},
            {field: "defaultInvoiceAddressLine1", rule: (v: any) => v == null || v.length <= 1000 || "DefaultInvoiceAddressLine1 has too many characters (1000)"},
            {field: "defaultInvoiceAddressLine2", rule: (v: any) => v == null || v.length <= 1000 || "DefaultInvoiceAddressLine2 has too many characters (1000)"},
            {field: "defaultInvoiceAddressLine3", rule: (v: any) => v == null || v.length <= 1000 || "DefaultInvoiceAddressLine3 has too many characters (1000)"},
            {field: "defaultInvoiceAddressLine4", rule: (v: any) => v == null || v.length <= 1000 || "DefaultInvoiceAddressLine4 has too many characters (1000)"},
            {field: "defaultInvoiceAddressLine5", rule: (v: any) => v == null || v.length <= 1000 || "DefaultInvoiceAddressLine5 has too many characters (1000)"},
            {field: "defaultInvoiceAddressLine6", rule: (v: any) => v == null || v.length <= 1000 || "DefaultInvoiceAddressLine6 has too many characters (1000)"},
            {field: "defaultInvoiceAddressPostcode", rule: (v: any) => v == null || v.length <= 1000 || "DefaultInvoiceAddressPostcode has too many characters (1000)"},
            {field: "countryCode", rule: (v: any) => v == null || v.length <= 1000 || "CountryCode has too many characters (1000)"},
            {field: "accountsDepartmentNumber", rule: (v: any) => v == null || v >= -2147483648 || "AccountsDepartmentNumber is not greater than minimum allowed value: -2147483648"},
            {field: "accountsDepartmentNumber", rule: (v: any) => v == null || v <= 2147483647 || "AccountsDepartmentNumber is above maximum allowed value: 2147483647"},
            {field: "defaultDespatchNoteTemplateId", rule: (v: any) => v == null || !v.isEmpty() || "Outlet.DefaultDespatchNoteTemplateId cannot be empty"},
            {field: "courierId", rule: (v: any) => v == null || !v.isEmpty() || "Outlet.CourierId cannot be empty"},
            {field: "deliveryAreaId", rule: (v: any) => v == null || !v.isEmpty() || "Outlet.DeliveryAreaId cannot be empty"},
            {field: "stopMinutes", rule: (v: any) => v == null || v >= -2147483648 || "StopMinutes is not greater than minimum allowed value: -2147483648"},
            {field: "stopMinutes", rule: (v: any) => v == null || v <= 2147483647 || "StopMinutes is above maximum allowed value: 2147483647"},
            {field: "deliveryNoteText", rule: (v: any) => v == null || v.length <= 100000 || "DeliveryNoteText has too many characters (100000)"},
            {field: "drayRunNotes", rule: (v: any) => v == null || v.length <= 100000 || "DrayRunNotes has too many characters (100000)"},
            {field: "eoriNumber", rule: (v: any) => v == null || v.length <= 1000 || "EoriNumber has too many characters (1000)"},
            {field: "exportingIncotermPlace", rule: (v: any) => v == null || v.length <= 1000 || "ExportingIncotermPlace has too many characters (1000)"},
            {field: "defaultDeliveryEmailAddress", rule: (v: any) => v == null || v.length <= 1000 || "DefaultDeliveryEmailAddress has too many characters (1000)"},
            {field: "defaultDeliveryTelephoneNumber", rule: (v: any) => v == null || v.length <= 1000 || "DefaultDeliveryTelephoneNumber has too many characters (1000)"},
            {field: "defaultDeliveryRecipient", rule: (v: any) => v == null || v.length <= 1000 || "DefaultDeliveryRecipient has too many characters (1000)"},
            {field: "defaultDeliveryAddressBusiness", rule: (v: any) => v == null || v.length <= 1000 || "DefaultDeliveryAddressBusiness has too many characters (1000)"},
            {field: "defaultDeliveryAddressLine1", rule: (v: any) => v == null || v.length <= 1000 || "DefaultDeliveryAddressLine1 has too many characters (1000)"},
            {field: "defaultDeliveryAddressLine2", rule: (v: any) => v == null || v.length <= 1000 || "DefaultDeliveryAddressLine2 has too many characters (1000)"},
            {field: "defaultDeliveryAddressLine3", rule: (v: any) => v == null || v.length <= 1000 || "DefaultDeliveryAddressLine3 has too many characters (1000)"},
            {field: "defaultDeliveryAddressLine4", rule: (v: any) => v == null || v.length <= 1000 || "DefaultDeliveryAddressLine4 has too many characters (1000)"},
            {field: "defaultDeliveryAddressLine5", rule: (v: any) => v == null || v.length <= 1000 || "DefaultDeliveryAddressLine5 has too many characters (1000)"},
            {field: "defaultDeliveryAddressLine6", rule: (v: any) => v == null || v.length <= 1000 || "DefaultDeliveryAddressLine6 has too many characters (1000)"},
            {field: "defaultDeliveryAddressPostcode", rule: (v: any) => v == null || v.length <= 1000 || "DefaultDeliveryAddressPostcode has too many characters (1000)"},
            {field: "vatCodeId", rule: (v: any) => !v.isEmpty() || "Outlet.VatCodeId cannot be empty"},
            {field: "tradingTerms", rule: (v: any) => v == null || v.length <= 100000 || "TradingTerms has too many characters (100000)"},
            {field: "companyRegNumber", rule: (v: any) => v == null || v.length <= 1000 || "CompanyRegNumber has too many characters (1000)"},
            {field: "salesNotes", rule: (v: any) => v == null || v.length <= 100000 || "SalesNotes has too many characters (100000)"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
            {field: "iban", rule: (v: any) => v == null || v.length <= 1000 || "Iban has too many characters (1000)"},
            {field: "bic", rule: (v: any) => v == null || v.length <= 1000 || "Bic has too many characters (1000)"},
            {field: "gkSupplierCode", rule: (v: any) => v == null || v.length <= 1000 || "GkSupplierCode has too many characters (1000)"},
            {field: "despatchNoteCopiesToPrint", rule: (v: any) => v == null || v >= 0 || "DespatchNoteCopiesToPrint is not greater than minimum allowed value: 0"},
            {field: "despatchNoteCopiesToPrint", rule: (v: any) => v == null || v <= 2147483647 || "DespatchNoteCopiesToPrint is above maximum allowed value: 2147483647"},
            {field: "invoiceCopiesToPrint", rule: (v: any) => v == null || v >= 0 || "InvoiceCopiesToPrint is not greater than minimum allowed value: 0"},
            {field: "invoiceCopiesToPrint", rule: (v: any) => v == null || v <= 2147483647 || "InvoiceCopiesToPrint is above maximum allowed value: 2147483647"},
            {field: "deliveryServiceId", rule: (v: any) => v == null || !v.isEmpty() || "Outlet.DeliveryServiceId cannot be empty"},
            {field: "invoiceNotes", rule: (v: any) => v == null || v.length <= 100000 || "InvoiceNotes has too many characters (100000)"},
            {field: "salesWarning", rule: (v: any) => v == null || v.length <= 100000 || "SalesWarning has too many characters (100000)"},
            {field: "liquorLicenceNumber", rule: (v: any) => v == null || v.length <= 1000 || "LiquorLicenceNumber has too many characters (1000)"},
            {field: "dispatchConfirmationWhatsAppNumber", rule: (v: any) => v == null || v.length <= 1000 || "DispatchConfirmationWhatsAppNumber has too many characters (1000)"},
            {field: "emailDespatchNotificationTemplateId", rule: (v: any) => v == null || !v.isEmpty() || "Outlet.EmailDespatchNotificationTemplateId cannot be empty"},
            {field: "despatchConfirmationEmailAddress", rule: (v: any) => v == null || v.length <= 1000 || "DespatchConfirmationEmailAddress has too many characters (1000)"},
            {field: "emailOrderConfirmationTemplateId", rule: (v: any) => v == null || !v.isEmpty() || "Outlet.EmailOrderConfirmationTemplateId cannot be empty"},
            {field: "orderConfirmationEmailAddress", rule: (v: any) => v == null || v.length <= 1000 || "OrderConfirmationEmailAddress has too many characters (1000)"},
            {field: "invoiceRecipientTaxNumber", rule: (v: any) => v == null || v.length <= 1000 || "InvoiceRecipientTaxNumber has too many characters (1000)"},
            {field: "productRequirements", rule: (v: any) => v == null || v.length <= 1000 || "ProductRequirements has too many characters (1000)"},
            {field: "webAddress", rule: (v: any) => v == null || v.length <= 1000 || "WebAddress has too many characters (1000)"},
            {field: "facebookWebAddress", rule: (v: any) => v == null || v.length <= 1000 || "FacebookWebAddress has too many characters (1000)"},
            {field: "instagramWebAddress", rule: (v: any) => v == null || v.length <= 1000 || "InstagramWebAddress has too many characters (1000)"},
            {field: "twitterWebAddress", rule: (v: any) => v == null || v.length <= 1000 || "TwitterWebAddress has too many characters (1000)"},
            {field: "linkedInWebAddress", rule: (v: any) => v == null || v.length <= 1000 || "LinkedInWebAddress has too many characters (1000)"},
            {field: "tikTokWebAddress", rule: (v: any) => v == null || v.length <= 1000 || "TikTokWebAddress has too many characters (1000)"},
            {field: "threadsWebAddress", rule: (v: any) => v == null || v.length <= 1000 || "ThreadsWebAddress has too many characters (1000)"},
            {field: "onHoldReason", rule: (v: any) => v == null || v.length <= 1000 || "OnHoldReason has too many characters (1000)"},
            {field: "awrsNumber", rule: (v: any) => v == null || v.length <= 1000 || "AwrsNumber has too many characters (1000)"},
            {field: "allowedDispatchDays", rule: (v: any) => v == null || v >= -2147483648 || "AllowedDispatchDays is not greater than minimum allowed value: -2147483648"},
            {field: "allowedDispatchDays", rule: (v: any) => v == null || v <= 2147483647 || "AllowedDispatchDays is above maximum allowed value: 2147483647"},
            {field: "customEmailSettingsId", rule: (v: any) => v == null || !v.isEmpty() || "Outlet.CustomEmailSettingsId cannot be empty"},
    ];
}


export class LatestCreditBalance extends ModelObject<LatestCreditBalance> {

    private _creditBalance: number | null = null;
    /**
    * Credit balance from the accounts package. Captured by system not user editable.
    * Not Negative
    */
    public get creditBalance(): number | null { return this._creditBalance }
    public set creditBalance(value: number | null) {
      this._creditBalance = value === undefined ? null : value;
    }
    private _creditBalanceExpiresAt: Instant | null = null;
    /**
    * The time when the credit balance will expire and need updating from the accounts package. Captured by system not user editable.
    */
    public get creditBalanceExpiresAt(): Instant | null { return this._creditBalanceExpiresAt }
    public set creditBalanceExpiresAt(value: Instant | null) {
      this._creditBalanceExpiresAt = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.creditBalance = jsonSerializedObject.creditBalance == null ? null : jsonSerializedObject.creditBalance;
          this.creditBalanceExpiresAt = jsonSerializedObject.creditBalanceExpiresAt == null ? null : Instant.parse(jsonSerializedObject.creditBalanceExpiresAt);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.creditBalance = this.creditBalance == null ? null : this.creditBalance;
        toRet.creditBalanceExpiresAt = this.creditBalanceExpiresAt == null ? null : this.creditBalanceExpiresAt.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
    ];
}


export class OutletCreditBalance extends ModelObject<OutletCreditBalance> {

    public outletId: Guid = Guid.createEmpty();
    private _creditBalance: number | null = null;
    public get creditBalance(): number | null { return this._creditBalance }
    public set creditBalance(value: number | null) {
      this._creditBalance = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.outletId = Guid.parseOrEmpty(jsonSerializedObject.outletId);
          this.creditBalance = jsonSerializedObject.creditBalance == null ? null : jsonSerializedObject.creditBalance;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.outletId = this.outletId.toString();
        toRet.creditBalance = this.creditBalance == null ? null : this.creditBalance;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "outletId", rule: (v: any) => !v.isEmpty() || "OutletCreditBalance.OutletId cannot be empty"},
    ];
}


export class OutletPackageType extends ModelObject<OutletPackageType> {

    public outletId: Guid = Guid.createEmpty();
    public packageTypeId: Guid = Guid.createEmpty();
    private _batchMinimumBestBeforeDays: number | null = null;
    /**
    * Used for product batch tracking
    */
    public get batchMinimumBestBeforeDays(): number | null { return this._batchMinimumBestBeforeDays }
    public set batchMinimumBestBeforeDays(value: number | null) {
      this._batchMinimumBestBeforeDays = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.outletId = Guid.parseOrEmpty(jsonSerializedObject.outletId);
          this.packageTypeId = Guid.parseOrEmpty(jsonSerializedObject.packageTypeId);
          this.batchMinimumBestBeforeDays = jsonSerializedObject.batchMinimumBestBeforeDays == null ? null : jsonSerializedObject.batchMinimumBestBeforeDays;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.outletId = this.outletId.toString();
        toRet.packageTypeId = this.packageTypeId.toString();
        toRet.batchMinimumBestBeforeDays = this.batchMinimumBestBeforeDays == null ? null : this.batchMinimumBestBeforeDays;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "outletId", rule: (v: any) => !v.isEmpty() || "OutletPackageType.OutletId cannot be empty"},
            {field: "packageTypeId", rule: (v: any) => !v.isEmpty() || "OutletPackageType.PackageTypeId cannot be empty"},
            {field: "batchMinimumBestBeforeDays", rule: (v: any) => v == null || v >= -2147483648 || "BatchMinimumBestBeforeDays is not greater than minimum allowed value: -2147483648"},
            {field: "batchMinimumBestBeforeDays", rule: (v: any) => v == null || v <= 2147483647 || "BatchMinimumBestBeforeDays is above maximum allowed value: 2147483647"},
    ];
}


export class OutletService extends ServiceBase {

  public async getAllOutletSummaries(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(OutletSummary)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => response.outletSummaries.map((v: any) => new OutletSummary(v));;
    return this.makeJsonRequest("outlet/v1/getAllOutletSummaries", req, false, extractor, opts);
  };

  public async getOutletSummaries(outletIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(OutletSummary)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    outletIds:outletIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.outletSummaries.map((v: any) => new OutletSummary(v));;
    return this.makeJsonRequest("outlet/v1/getOutletSummaries", req, false, extractor, opts);
  };

  public async getAllOutlets(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(Outlet)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => response.outlets.map((v: any) => new Outlet(v));;
    return this.makeJsonRequest("outlet/v1/getAllOutlets", req, false, extractor, opts);
  };

  public async getOutlets(outletId: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(Outlet)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    outletId:outletId.map(v => v.toString()),
    };
    const extractor = (response:any) => response.outlets.map((v: any) => new Outlet(v));;
    return this.makeJsonRequest("outlet/v1/getOutlets", req, false, extractor, opts);
  };

  public async getOutlet(outletId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Outlet> {
    const req = {
    tenantId:api.tenantId.toString(),
    outletId:outletId.toString(),
    };
    const extractor = (response:any) => new Outlet(response.outlet);;
    return this.makeJsonRequest("outlet/v1/getOutlet", req, false, extractor, opts);
  };

  public async createOutlet(outlet: Outlet, outletIsRetail: boolean, contacts: readonly Contact[], outletCode: string, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    outlet:outlet.toJsonSerializedObject(),
    outletIsRetail:outletIsRetail,
    contacts:contacts.map(v => v.toJsonSerializedObject()),
    outletCode:outletCode,
    };
    const extractor = null;
    return this.makeJsonRequest("outlet/v1/createOutlet", req, true, extractor, opts);
  };

  public async updateOutlet(outletId: Guid, outlet: Outlet, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    outletId:outletId.toString(),
    outlet:outlet.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("outlet/v1/updateOutlet", req, true, extractor, opts);
  };

  public async setOutletIsHidden(outletId: Guid, hide: boolean, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    outletId:outletId.toString(),
    hide:hide,
    };
    const extractor = null;
    return this.makeJsonRequest("outlet/v1/setOutletIsHidden", req, true, extractor, opts);
  };

  public async generateOutletCode(outletName: string, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<string> {
    const req = {
    tenantId:api.tenantId.toString(),
    outletName:outletName,
    };
    const extractor = (response:any) => response.code;;
    return this.makeJsonRequest("outlet/v1/generateOutletCode", req, false, extractor, opts);
  };

  public async getLatestCreditBalance(outletId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<LatestCreditBalance> {
    const req = {
    tenantId:api.tenantId.toString(),
    outletId:outletId.toString(),
    };
    const extractor = (response:any) => new LatestCreditBalance(response.latestCreditBalance);;
    return this.makeJsonRequest("outlet/v1/getLatestCreditBalance", req, false, extractor, opts);
  };

  public async updateCreditBalances(outletCreditBalances: readonly OutletCreditBalance[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    outletCreditBalances:outletCreditBalances.map(v => v.toJsonSerializedObject()),
    };
    const extractor = null;
    return this.makeJsonRequest("outlet/v1/updateCreditBalances", req, true, extractor, opts);
  };

  public async getContacts(outletId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(Contact)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    outletId:outletId.toString(),
    };
    const extractor = (response:any) => response.contacts.map((v: any) => new Contact(v));;
    return this.makeJsonRequest("outlet/v1/getContacts", req, false, extractor, opts);
  };

  public async getMultipleOutletContacts(outletIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(Contact)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    outletIds:outletIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.contacts.map((v: any) => new Contact(v));;
    return this.makeJsonRequest("outlet/v1/getMultipleOutletContacts", req, false, extractor, opts);
  };

  public async saveContacts(contacts: readonly Contact[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    contacts:contacts.map(v => v.toJsonSerializedObject()),
    };
    const extractor = null;
    return this.makeJsonRequest("outlet/v1/saveContacts", req, true, extractor, opts);
  };

  public async deleteContacts(contactIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    contactIds:contactIds.map(v => v.toString()),
    };
    const extractor = null;
    return this.makeJsonRequest("outlet/v1/deleteContacts", req, true, extractor, opts);
  };

  public async getAllContacts(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(Contact)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => response.contacts.map((v: any) => new Contact(v));;
    return this.makeJsonRequest("outlet/v1/getAllContacts", req, false, extractor, opts);
  };

  public async getPrimaryContact(outletId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Contact | null> {
    const req = {
    tenantId:api.tenantId.toString(),
    outletId:outletId.toString(),
    };
    const extractor = (response:any) => response.contact == null ? null : new Contact(response.contact);;
    return this.makeJsonRequest("outlet/v1/getPrimaryContact", req, false, extractor, opts);
  };

  public async getPrimaryContactsForOutlets(outletIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(Contact)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    outletIds:outletIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.contacts.map((v: any) => new Contact(v));;
    return this.makeJsonRequest("outlet/v1/getPrimaryContactsForOutlets", req, false, extractor, opts);
  };

  public async generateSwedishRestaurantReport(outletId: Guid, reportDateFrom: LocalDate, reportDateTo: LocalDate, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<string> {
    const req = {
    tenantId:api.tenantId.toString(),
    outletId:outletId.toString(),
    reportDateFrom:reportDateFrom.toString(),
    reportDateTo:reportDateTo.toString(),
    };
    const extractor = (response:any) => response.csvContent;;
    return this.makeJsonRequest("outlet/v1/generateSwedishRestaurantReport", req, false, extractor, opts);
  };

  public async generateSwedishRestaurantAudit(outletId: Guid, reportDateFrom: LocalDate, reportDateTo: LocalDate, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<string> {
    const req = {
    tenantId:api.tenantId.toString(),
    outletId:outletId.toString(),
    reportDateFrom:reportDateFrom.toString(),
    reportDateTo:reportDateTo.toString(),
    };
    const extractor = (response:any) => response.csvContent;;
    return this.makeJsonRequest("outlet/v1/generateSwedishRestaurantAudit", req, false, extractor, opts);
  };

  public async getAllOutletPackageTypesForOutlets(outletIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(OutletPackageType)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    outletIds:outletIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.outletPackageTypes.map((v: any) => new OutletPackageType(v));;
    return this.makeJsonRequest("outlet/v1/getAllOutletPackageTypesForOutlets", req, false, extractor, opts);
  };

  public async saveOutletPackageTypes(outletId: Guid, outletPackageTypes: readonly OutletPackageType[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    outletId:outletId.toString(),
    outletPackageTypes:outletPackageTypes.map(v => v.toJsonSerializedObject()),
    };
    const extractor = null;
    return this.makeJsonRequest("outlet/v1/saveOutletPackageTypes", req, true, extractor, opts);
  };
}

export const outletService = new OutletService();